import { Component, signal, HostListener, OnInit, OnDestroy, afterNextRender, EnvironmentInjector, ErrorHandler, Injectable, runInInjectionContext, inject } from '@angular/core';
import { StreakService } from './services/streak.service';
import { AchievementsService } from './services/achievements.service';
import { LevelsService } from './services/levels.service';
import { MedalCelebrationComponent } from './shared/components/medal-celebration/medal-celebration.component';
import { OrbComponent } from './shared/components/orb/orb.component';
import { CelebrationHostComponent } from './shared/components/celebration-host/celebration-host.component';
import { CelebrationService } from './services/celebration.service';
import { BreakyCompanionComponent } from './shared/components/breaky-companion/breaky-companion.component';
import { BreakyCompanionService } from './services/breaky-companion.service';
import { QuestsService } from './services/quests.service';
import { FirstStepsService } from './services/first-steps.service';
import { OverlayLockService } from './services/overlay-lock.service';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule, NgIf } from '@angular/common';
import { AuthService } from './services/auth.service';
import { SessionManagerService } from './services/session-manager.service';
import { NotificationService } from './services/notification.service';
import { PushService } from './services/push.service';
import { LoggerService } from './services/logger.service';
import { FirestoreService } from './services/firestore.service';
import { Subject, Subscription, Observable, firstValueFrom, fromEvent } from 'rxjs'; // Combined Observable import
import { takeUntil, filter } from 'rxjs/operators';
import { NavigationComponent } from './navigation/navigation.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AvatarTheme, resolveAvatarTheme, THEMES } from './shared/avatar/avatar.config';
import { resolveAvatarPresetId } from './shared/avatar/avatar-presets';
import { Chart, registerables } from 'chart.js';
import { DailyCheckinModalComponent } from './daily-checkin-modal/daily-checkin-modal.component';
import { ClimatePulseModalComponent } from './climate-pulse-modal/climate-pulse-modal.component';
import { ClimatePulseService } from './services/climate-pulse.service';
import { PushPromptModalComponent } from './shared/components/push-prompt-modal/push-prompt-modal.component';
import { PasswordPromptModalComponent } from './shared/components/password-prompt-modal/password-prompt-modal.component';
import { DatesPromptModalComponent } from './shared/components/dates-prompt-modal/dates-prompt-modal.component';
import { CelebrationDatesService } from './services/celebration-dates.service';
import { DatesNeeded, canRequestDatesPrompt, pauseCompleted, returnedToApp } from './shared/celebration-dates/celebration-dates.rules';
import { InstallAppBannerComponent } from './shared/components/install-app-banner/install-app-banner.component';
import { PwaInstallService } from './services/pwa-install.service';
import { IosInstallBannerComponent } from './shared/components/ios-install-banner/ios-install-banner.component';
import { IOS_HINT_STORAGE_KEY, readHintEnvironment, shouldShowIosInstallHint } from './shared/utils/ios-push-hint';
import { SmartScoreModalComponent } from './shared/components/smart-score-modal/smart-score-modal.component';
import { SmartScoreWidgetComponent } from './shared/components/smart-score-widget/smart-score-widget.component';
import { TourService } from './shared/tour/tour.service';
import { NotificationsBellComponent } from './shared/components/notifications-bell/notifications-bell.component';
import { GameInviteBannerComponent } from './shared/components/game-invite-banner/game-invite-banner.component';
import { OnlineUsersComponent } from './shared/components/online-users/online-users.component';
import { GamificationService } from './services/gamification.service';
import { ExerciseService } from './services/exercise.service';
import { WorkplaceErgonomicsService } from './services/workplace-ergonomics.service';
import { DoNotDisturbService } from './services/do-not-disturb.service';
// Removed: workspace, workdays and start-time modals (data collected during registration steps)
import { IonicModule, MenuController } from '@ionic/angular';
import { NativeBridgeService } from './services/native-bridge.service';
import { LanguageService } from './services/language.service';
import { TranslateModule } from '@ngx-translate/core';
import { UpdateBannerComponent } from './shared/components/update-banner/update-banner.component';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NavigationComponent,
    FontAwesomeModule,
    DailyCheckinModalComponent,
    ClimatePulseModalComponent,
    PushPromptModalComponent,
    PasswordPromptModalComponent,
    DatesPromptModalComponent,
    IosInstallBannerComponent,
    InstallAppBannerComponent,
    SmartScoreModalComponent,
    SmartScoreWidgetComponent,
    NotificationsBellComponent,
    GameInviteBannerComponent,
    OnlineUsersComponent,
    IonicModule,
    TranslateModule,
    UpdateBannerComponent,
    MedalCelebrationComponent,
    CelebrationHostComponent,
    BreakyCompanionComponent,
    OrbComponent
  ],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  private readonly streakService = inject(StreakService);
  private readonly achievementsService = inject(AchievementsService);
  private readonly celebrationService = inject(CelebrationService);
  private readonly companionService = inject(BreakyCompanionService);
  private readonly questsService = inject(QuestsService);
  private readonly firstSteps = inject(FirstStepsService);
  private readonly celebrationDates = inject(CelebrationDatesService);
  readonly overlayLock = inject(OverlayLockService);
  private readonly levelsService = inject(LevelsService);
  readonly climatePulse = inject(ClimatePulseService);
  protected readonly title = signal('SmartBreak');
  username: string = 'Usuario';
  userAvatarKey: string | null = null;
  userAvatarPhoto: string | null = null;
  userWorkTypeLabel: string | null = null;
  userPoints: number | null = null;
  isLoggedIn: boolean = false;
  /** true mientras el usuario está en el flujo de autenticación (/auth). */
  isFullScreenRoute = false;
  readonly isMenuOpen = signal(false);
  showUserGreeting: boolean = false;
  errorMessage: string | null = null;
  showDailyCheckinModal = false;
  showPushPromptModal = false;
  showPasswordPromptModal = false;
  showIosInstallHint = false;
  currentUserIdForPrompt: string | null = null;
  showSplash = true;
  private pendingDailyCheckin = false;
  private climateQuestionOpen = false;
  private tourScheduledFor: string | null = null;
  /** Aviso «completa tu cumpleaños y aniversario»: qué falta, y si ya está programado para esta sesión. */
  showDatesPromptModal = false;
  datesNeeded: DatesNeeded = { birthday: false, hire: false };
  private pendingDatesPrompt = false;
  /** Quién está en sesión (para volver a pedir sin repetir el login) y si ya hay una consulta o espera en curso. */
  private datesUser: { uid: string; hasCompany: boolean } | null = null;
  private datesPromptBusy = false;
  private datesLastEmptyAt: number | null = null;
  private celebrationActive = false;
  private lastPausesToday: number | null = null;
  private hiddenAt: number | null = null;
  private readonly tour = inject(TourService);
  private pendingPushPrompt = false;
  private pendingPasswordPrompt = false;
  private passwordPromptShown = false;

  


  private destroy$ = new Subject<void>();
  private profileSubscription: Subscription | null = null;
  private userProfileSubscription: Subscription | null = null;
  private foregroundUnsubscribe: (() => void) | null = null;
  private splashHideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly splashHideDelayMs = 1200;
  private splashNavigationLocked = false;
  private splashNavigationTarget: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly firestoreService: FirestoreService,
    private readonly pushService: PushService,
    private readonly notificationService: NotificationService,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly nativeBridge: NativeBridgeService,
    private readonly menuCtrl: MenuController,
    private readonly languageService: LanguageService,
    private readonly sessionManager: SessionManagerService,
    private readonly ergonomicsService: WorkplaceErgonomicsService,
    private readonly doNotDisturbService: DoNotDisturbService,
    private readonly exerciseService: ExerciseService,
    readonly gamificationService: GamificationService,
    // Se crea al arrancar para no perder el evento beforeinstallprompt (se emite una sola vez, pronto).
    private readonly pwaInstall: PwaInstallService
  ) {
    Chart.register(...registerables);
    this.updateMenuAvailability(this.isLoggedIn);
    this.updateFullScreenRoute(this.router.url);
    this.languageService.init();
  }

  /**
   * Si un superAdmin restableció la contraseña (temporal), sugiere cambiarla. Se muestra una vez
   * por sesión y solo cuando el usuario ya está dentro del dashboard.
   */
  private async showPasswordPromptIfReady(): Promise<void> {
    if (!this.pendingPasswordPrompt || !this.router.url.startsWith('/dashboard')) return;
    this.pendingPasswordPrompt = false;
    this.passwordPromptShown = true;
    this.showPasswordPromptModal = true;
  }

  /** iPhone/iPad con la PWA sin instalar: no hay Web Push hasta agregarla a la pantalla de inicio. */
  private evaluateIosInstallHint(): boolean {
    const env = readHintEnvironment();
    if (!env) return false;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(IOS_HINT_STORAGE_KEY);
    } catch {
      // sin localStorage: se muestra
    }
    return shouldShowIosInstallHint(env, stored);
  }

  closePasswordPrompt(): void {
    this.showPasswordPromptModal = false;
  }

  /**
   * Pide el cumpleaños y el aniversario a quien aún no los registró (antes no eran obligatorios). Se pide cuando la
   * persona abre la app (con la sesión ya iniciada o no), vuelve a ella tras un rato, cierra el check-in de ánimo o
   * termina una pausa. Consulta qué falta y espera a que no haya otras ventanas abiertas para no encimarse.
   */
  private requestDatesPrompt(delayMs = 0): void {
    const user = this.datesUser;
    if (
      !user ||
      !canRequestDatesPrompt({
        hasCompany: user.hasCompany,
        showing: this.showDatesPromptModal,
        busy: this.datesPromptBusy,
        lastEmptyAt: this.datesLastEmptyAt,
        now: Date.now()
      })
    ) {
      return;
    }
    void this.runDatesPrompt(user.uid, delayMs);
  }

  private async runDatesPrompt(uid: string, delayMs: number): Promise<void> {
    this.datesPromptBusy = true;
    try {
      if (delayMs > 0) await new Promise((r) => setTimeout(r, delayMs));
      const needed = await this.celebrationDates.evaluate(uid, true);
      if (this.datesUser?.uid !== uid) return;
      if (!needed) {
        this.datesLastEmptyAt = Date.now();
        return;
      }
      this.pendingDatesPrompt = true;
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        if (this.datesUser?.uid !== uid) return; // cerró sesión o cambió de cuenta
        const overlayOpen = await firstValueFrom(this.overlayLock.open$);
        const busy =
          this.showSplash ||
          this.showDailyCheckinModal ||
          this.pendingDailyCheckin ||
          this.showPushPromptModal ||
          this.pendingPushPrompt ||
          this.showPasswordPromptModal ||
          this.pendingPasswordPrompt ||
          this.climateQuestionOpen ||
          this.celebrationActive ||
          this.tour.isRunning ||
          overlayOpen;
        if (!busy && this.router.url.startsWith('/dashboard')) {
          this.datesNeeded = needed;
          this.showDatesPromptModal = true;
          return;
        }
      }
      // No hubo un momento tranquilo: se vuelve a intentar en el próximo disparador (volver a la app, check-in, pausa).
    } finally {
      this.pendingDatesPrompt = false;
      this.datesPromptBusy = false;
    }
  }

  /** Momentos en que se vuelve a pedir: volver a la app tras un rato y terminar una pausa (el check-in va en su cierre). */
  private watchDatesTriggers(): void {
    this.celebrationService.current$.pipe(takeUntil(this.destroy$)).subscribe((e) => (this.celebrationActive = !!e));

    this.gamificationService.summary$.pipe(takeUntil(this.destroy$)).subscribe((summary) => {
      const pauses = summary?.daily?.vitalisPausesToday ?? null;
      // Se espera unos segundos: primero se celebra la recompensa de la pausa y después se pide.
      if (pauseCompleted(this.lastPausesToday, pauses)) this.requestDatesPrompt(4000);
      this.lastPausesToday = pauses;
    });

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState === 'hidden') {
          this.hiddenAt = Date.now();
          return;
        }
        if (returnedToApp(this.hiddenAt, Date.now())) this.requestDatesPrompt(1500);
        this.hiddenAt = null;
      });
  }

  closeDatesPrompt(): void {
    this.showDatesPromptModal = false;
  }

  closeSmartScoreModal(): void {
    this.gamificationService.closeBreakdown();
  }

  closeGiveKarmaModal(): void {
    this.gamificationService.closeGiveKarma();
  }

  onKarmaSent(): void {
    void this.gamificationService.refresh();
  }

  onMenuWillOpen(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      this.isMenuOpen.set(true);
    }
  }

  onMenuDidOpen(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      this.isMenuOpen.set(true);
    }
  }

  onMenuWillClose(): void {
    this.isMenuOpen.set(false);
  }

  onMenuDidClose(): void {
    this.isMenuOpen.set(false);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 992 && this.isMenuOpen()) {
      this.isMenuOpen.set(false);
    }
  }

  private updateMenuAvailability(isEnabled: boolean): void {
    this.menuCtrl.enable(isEnabled).catch(() => {});
    if (!isEnabled) {
      this.isMenuOpen.set(false);
      this.menuCtrl.close().catch(() => {});
    }
  }

  private updateSplashNavigation(user: any): void {
    if (this.splashNavigationLocked) {
      return;
    }

    const target = user ? this.landingUrlForUser() : this.landingUrlForGuest();

    if (target !== null && this.splashNavigationTarget !== target) {
      this.splashNavigationTarget = target;
      this.router.navigateByUrl(target, { replaceUrl: true }).catch(() => {});
      if (this.splashHideTimeoutId !== null) {
        this.clearSplashHideTimeout();
      }
    }

    this.scheduleSplashHide();
  }

  /**
   * Rutas públicas a las que se puede llegar por enlace directo (auth y páginas
   * legales). En ellas **no** se reescribe la URL: si no, el arranque mandaría a
   * `/auth` (o al panel) y el enlace directo se perdería.
   */
  private static readonly PUBLIC_URL_PREFIXES = ['/auth', '/legal', '/terms', '/privacy'];

  private isPublicUrl(): boolean {
    // Se usa la URL del navegador (no `router.url`) porque en el arranque la
    // navegación inicial todavía puede no haber terminado.
    const path = (window.location.pathname || '/').toLowerCase();
    return App.PUBLIC_URL_PREFIXES.some(
      prefix => path === prefix || path.startsWith(`${prefix}/`)
    );
  }

  /** `null` = respetar la URL actual. */
  private landingUrlForUser(): string | null {
    return this.isPublicUrl() ? null : '/dashboard/home';
  }

  /** `null` = respetar la URL actual. */
  private landingUrlForGuest(): string | null {
    return this.isPublicUrl() ? null : '/auth';
  }

  private scheduleSplashHide(delay = this.splashHideDelayMs): void {
    if (!this.showSplash || this.splashHideTimeoutId !== null) {
      return;
    }

    this.splashHideTimeoutId = setTimeout(() => {
      this.showSplash = false;
      this.splashHideTimeoutId = null;
      this.splashNavigationLocked = true;
    }, delay);
  }

  private clearSplashHideTimeout(): void {
    if (this.splashHideTimeoutId !== null) {
      clearTimeout(this.splashHideTimeoutId);
      this.splashHideTimeoutId = null;
    }
  }

  /**
   * P-007 Fix: Salvaguarda de hidratación Ionic/Stencil.
   * En recargas duras (F5) con sesión activa, si el ciclo de Stencil se retrasa
   * o se bloquea esperando componentes hijos, garantizamos que ion-app y
   * las páginas de ion-router-outlet nunca queden permanentemente invisibles.
   */
  private ensureHydrationSafetyNet(): void {
    if (typeof window === 'undefined') return;

    // Red de seguridad a los 800ms (antes de ocultar el splash a los 1200ms)
    setTimeout(() => {
      try {
        const ionApp = document.querySelector('ion-app');
        if (ionApp && !ionApp.classList.contains('hydrated')) {
          ionApp.classList.add('hydrated', 'hydrated-fallback');
        }
        document.body.classList.add('ion-hydrated-fallback');

        // Retirar ion-page-invisible residual si la animación de transición no disparó
        const invisiblePages = document.querySelectorAll('.ion-page-invisible');
        invisiblePages.forEach(el => el.classList.remove('ion-page-invisible'));
      } catch (err) {
        console.warn('[HydrationSafety] Error applying fallback:', err);
      }
    }, 800);
  }


  ngOnInit(): void {
    this.watchDatesTriggers();
    this.climatePulse.question$.pipe(takeUntil(this.destroy$)).subscribe((q) => (this.climateQuestionOpen = !!q));
    // Fallback: Ensure splash screen never stays stuck permanently
    this.scheduleSplashHide(1500);
    this.ensureHydrationSafetyNet();

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async user => {
        this.isLoggedIn = !!user;
        this.updateSplashNavigation(user);
        this.updateMenuAvailability(this.isLoggedIn);
        if (user) {
          const fallbackName = user.email || 'Usuario';
          this.username = this.buildShortName(null, null, fallbackName);
          this.subscribeToUserProfile(user.uid, fallbackName);
          this.subscribeToUserPoints(user.uid);

          this.currentUserIdForPrompt = user.uid;
          this.showIosInstallHint = this.evaluateIosInstallHint();
          // Catalogo de pausas del usuario (segun su empresa y tipo de trabajo).
          void this.exerciseService.loadRemoteCatalog();
          // Racha de dias de uso: cuenta el dia de hoy.
          void this.streakService.register(user.uid);
          // Medallas: revisa las nuevas y las celebra.
          this.achievementsService.start();
          // Celebraciones: subida de nivel, karma recibido y puntos ganados.
          this.celebrationService.start();
          // Breaky flotante: consejos y avisos con calma.
          this.companionService.start(user.uid);
          // Misiones diarias.
          this.questsService.start();
          // Primeros pasos: checklist de bienvenida.
          this.firstSteps.start(user.uid, user.createdAt);
          // Escala de niveles definida en la consola.
          void this.levelsService.load();
          this.pendingPasswordPrompt = !!user.mustChangePassword && !this.passwordPromptShown;
          if (!user.mustChangePassword) this.passwordPromptShown = false;
          void this.showPasswordPromptIfReady();

          // Régimen ergonómico contextual (Actividad 2): deriva el modo del puesto y arranca la jornada.
          this.ergonomicsService.applyUserContext(user.workType);
          this.ergonomicsService.ensureSessionRunning();
          // Modo Reunion: reenvia al servidor el estado vigente (p. ej. activado antes de iniciar sesion).
          this.doNotDisturbService.syncToServer();

          // Notificaciones in-app (campana) en tiempo real
          // Tour guiado: solo la primera vez, cuando ya no hay ventanas de arranque abiertas.
          if (this.tourScheduledFor !== user.uid) {
            this.tourScheduledFor = user.uid;
            void this.tour.autoStartIfFirstTime(
              () =>
                this.showSplash ||
                this.showDailyCheckinModal ||
                this.pendingDailyCheckin ||
                this.showPushPromptModal ||
                this.pendingPushPrompt ||
                this.showPasswordPromptModal ||
                this.pendingPasswordPrompt ||
                this.showDatesPromptModal ||
                this.pendingDatesPrompt ||
                this.climateQuestionOpen,
              // Breaky ofrece el recorrido (la checklist de Primeros pasos lo recoge si lo ignora).
              () => this.firstSteps.offerTour()
            );
          }

          // Cumpleaños y aniversario: al abrir la app (con la sesión ya iniciada o recién iniciada).
          const firstSeen = this.datesUser?.uid !== user.uid;
          this.datesUser = { uid: user.uid, hasCompany: !!user.companyId };
          if (firstSeen) this.requestDatesPrompt();

          this.notificationService.connectRealtime({
            displayName: user.displayName || user.firstNames || user.email?.split('@')[0],
            avatarPreset: user.avatarPreset,
            photoUrl: user.photoURL || user.photoObjectName
          });
          void this.notificationService.loadFeed();

          const shouldShow = await this.firestoreService.shouldShowDailyCheckin(user.uid);
          // Show daily check-in modal after registration if flag is set
          let showAfterRegister = false;
          if (typeof window !== 'undefined' && window.localStorage) {
            showAfterRegister = window.localStorage.getItem('showDailyCheckinAfterRegister') === 'true';
            if (showAfterRegister) {
              window.localStorage.removeItem('showDailyCheckinAfterRegister');
            }
          }
          if (shouldShow || showAfterRegister) {
            this.requestDailyCheckin();
            this.showDailyCheckinIfReady();
          } else {
            this.cancelDailyCheckinRequest();
            this.checkPushPromptEligibility(user.uid);
            this.scheduleClimatePulse();
          }
        } else {
          this.climatePulse.reset();
          this.tourScheduledFor = null;
          this.datesUser = null;
          this.datesLastEmptyAt = null;
          this.lastPausesToday = null;
          this.pendingDatesPrompt = false;
          this.showDatesPromptModal = false;
          this.notificationService.disconnect();
          this.celebrationService.stop();
          this.companionService.stop();
          this.questsService.stop();
          this.firstSteps.stop();
          this.username = 'Usuario';
          this.userAvatarKey = null;
          this.userAvatarPhoto = null;
          this.userWorkTypeLabel = null;
          this.userPoints = null;
          this.cancelDailyCheckinRequest();
          this.showPushPromptModal = false;
          this.pendingPushPrompt = false;
          this.pendingPasswordPrompt = false;
          this.passwordPromptShown = false;
          this.showPasswordPromptModal = false;
          this.showIosInstallHint = false;
          this.exerciseService.clearRemoteCatalog();
          this.currentUserIdForPrompt = null;
          // onboarding flow removed
          this.teardownProfileSubscription();
          this.teardownUserPointsSubscription();
        }
        this.updateShowUserGreeting(this.router.url);
      });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      const currentUrl = event.urlAfterRedirects ?? event.url;
      this.sessionManager.touchActivity();
      this.updateShowUserGreeting(currentUrl);
      this.updateFullScreenRoute(currentUrl);
      if (currentUrl.startsWith('/dashboard')) {
        void this.showPasswordPromptIfReady();
        this.showDailyCheckinIfReady();
        if (!this.showDailyCheckinModal && !this.pendingDailyCheckin && this.pendingPushPrompt) {
          this.showPushPromptIfReady();
        }
      }
    });

    runInInjectionContext(this.environmentInjector, () => {
      afterNextRender(() => {
        if (typeof Notification === 'undefined') {
          return;
        }
        this.foregroundUnsubscribe = this.pushService.registerForegroundHandler(async (payload: any) => {
          try {
            // Modo Reunión / No Molestar: no interrumpir mientras esté vigente.
            if (this.doNotDisturbService.isActive()) {
              return;
            }
            const title = payload?.notification?.title || 'SmartBreak';
            const body = payload?.notification?.body || 'Tienes una nueva notificacion.';
            const options: NotificationOptions = {
              body,
              icon: '/assets/icons/icon-192x192.png',
              data: payload?.data || {}
            };
            if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
              const reg = await navigator.serviceWorker.getRegistration();
              if (reg && Notification.permission === 'granted') {
                reg.showNotification(title, options);
                return;
              }
            }
            if (Notification.permission === 'granted') {
              new Notification(title, options);
            }
          } catch (error) {
            console.warn('Foreground notification display failed:', error);
          }
        });
      });
    });
  }

  /** Pulso de clima: despues del check-in y las ventanas de arranque, nunca encima de ellas. */
  private scheduleClimatePulse(): void {
    setTimeout(() => {
      if (this.showDailyCheckinModal || this.pendingDailyCheckin || this.showPushPromptModal || this.pendingPushPrompt) return;
      if (!this.router.url.startsWith('/dashboard')) return;
      void this.climatePulse.promptIfDue();
    }, 2500);
  }

  closeDailyCheckinModal(): void {
    this.cancelDailyCheckinRequest();
    this.scheduleClimatePulse();
    this.requestDatesPrompt(1500);
    if (this.currentUserIdForPrompt) {
      setTimeout(() => {
        if (this.currentUserIdForPrompt) {
          this.checkPushPromptEligibility(this.currentUserIdForPrompt);
        }
      }, 200);
    }
  }

  onPushPromptClosed(granted: boolean): void {
    this.showPushPromptModal = false;
    this.pendingPushPrompt = false;
  }

  private checkPushPromptEligibility(uid: string): void {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      return;
    }

    if (Notification.permission === 'granted') {
      this.pushService.ensureTokenSync(uid).catch(() => {});
      this.showPushPromptModal = false;
      this.pendingPushPrompt = false;
      return;
    }

    if (Notification.permission === 'denied') {
      this.showPushPromptModal = false;
      this.pendingPushPrompt = false;
      return;
    }

    // Si aún no se ha solicitado (Notification.permission === 'default')
    const handled = localStorage.getItem('sb_push_prompt_handled_' + uid);
    if (handled) {
      this.showPushPromptModal = false;
      this.pendingPushPrompt = false;
      return;
    }

    if (this.showDailyCheckinModal || this.pendingDailyCheckin) {
      this.pendingPushPrompt = true;
      return;
    }

    this.pendingPushPrompt = true;
    this.showPushPromptIfReady();
  }

  private showPushPromptIfReady(): void {
    if (!this.pendingPushPrompt || this.showDailyCheckinModal || this.pendingDailyCheckin) {
      return;
    }
    const currentUrl = this.router.url;
    if (!currentUrl.startsWith('/dashboard')) {
      return;
    }
    setTimeout(() => {
      if (!this.showDailyCheckinModal && !this.pendingDailyCheckin) {
        this.showPushPromptModal = true;
        this.pendingPushPrompt = false;
      }
    }, 200);
  }

  private requestDailyCheckin(): void {
    this.pendingDailyCheckin = true;
    this.showDailyCheckinModal = false;
  }

  private cancelDailyCheckinRequest(): void {
    this.pendingDailyCheckin = false;
    this.showDailyCheckinModal = false;
  }

  private showDailyCheckinIfReady(): void {
    if (!this.pendingDailyCheckin) {
      return;
    }
    // onboarding checks removed
    const currentUrl = this.router.url;
    if (!currentUrl.startsWith('/dashboard')) {
      return;
    }
    // Forzar el trigger con pequeño retraso para asegurar render
    setTimeout(() => {
      this.showDailyCheckinModal = true;
      this.pendingDailyCheckin = false;
    }, 100);
  }

  private subscribeToUserProfile(uid: string, fallbackName: string): void {
    this.teardownProfileSubscription();
    this.profileSubscription = this.firestoreService
      .getUserIdentity(uid, fallbackName)
      .subscribe((identity: {
        displayName: string;
        firstNames: string | null;
        lastNames: string | null;
        fullDisplayName: string | null;
        avatar: string | null;
        customAvatar: string | null;
      }) => {
        this.username = this.buildShortName(identity.firstNames, identity.lastNames, identity.displayName);
        const rawAvatar = typeof identity.avatar === 'string' ? identity.avatar.trim() : '';
        const avatarKey = rawAvatar === 'custom'
          ? 'custom'
          : (resolveAvatarPresetId(rawAvatar) ?? 'avatar1');
        this.userAvatarKey = avatarKey;
        this.userAvatarPhoto = typeof identity.customAvatar === 'string' && identity.customAvatar.trim()
          ? identity.customAvatar.trim()
          : null;
      });
  }

  private buildShortName(firstNames: string | null, lastNames: string | null, fallback: string | null | undefined): string {
    const normalizedFirst = this.normalizeNameSegment(firstNames);
    const normalizedLast = this.normalizeNameSegment(lastNames);

    if (normalizedFirst) {
      const firstToken = normalizedFirst.split(' ')[0];
      const lastToken = normalizedLast ? normalizedLast.split(' ')[0] : '';
      const composed = [firstToken, lastToken].filter(Boolean).join(' ').trim();
      if (composed) {
        return composed;
      }
    }

    const normalizedFallback = this.normalizeNameSegment(fallback);
    if (normalizedFallback) {
      const parts = normalizedFallback.split(' ').filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0]} ${parts[1]}`;
      }
      return parts[0];
    }

    return 'Usuario';
  }

  private normalizeNameSegment(value: string | null | undefined): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim().replace(/\s+/g, ' ');
  }

  private subscribeToUserPoints(uid: string): void {
    this.teardownUserPointsSubscription();
    this.userProfileSubscription = this.firestoreService.getUserProfile(uid)
      .subscribe(profile => {
          this.userPoints = profile?.puntos || 0;
        const rawType = (profile?.workType ?? '').toString().trim().toLowerCase();
        let key: 'administrativo' | 'técnico' | null = null;
        if (rawType === 'administrativo') {
          key = 'administrativo';
        } else if (rawType === 'técnico') {
          key = 'técnico';
        }
        this.userWorkTypeLabel = key === 'técnico' ? 'Técnico' : key === 'administrativo' ? 'Administrativo' : null;
        // onboarding flow removed (data collected during registration steps)
      });
  }

  // Onboarding flow removed: data is collected at registration steps
  private teardownProfileSubscription(): void {
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
      this.profileSubscription = null;
    }
  }

  private teardownUserPointsSubscription(): void {
    if (this.userProfileSubscription) {
      this.userProfileSubscription.unsubscribe();
      this.userProfileSubscription = null;
    }
  }

  private updateShowUserGreeting(url: string): void {
    const hiddenRoutes = ['/login', '/register'];
    this.showUserGreeting = this.isLoggedIn && !hiddenRoutes.includes(url);
  }

  /**
   * Chrome global (menú lateral + barra de puntos/notificaciones): solo con sesión
   * activa **y** fuera de las rutas a pantalla completa. Evita que la barra aparezca
   * durante el registro (p. ej. en la bienvenida antes de pulsar "¡Iniciar ya!") y
   * en las páginas legales.
   */
  get showGlobalChrome(): boolean {
    return this.isLoggedIn && !this.isFullScreenRoute;
  }

  private updateFullScreenRoute(url: string): void {
    const path = url || '';
    this.isFullScreenRoute = path.startsWith('/auth') || path.startsWith('/legal');
  }

  ngOnDestroy(): void {
    this.teardownProfileSubscription();
    this.teardownUserPointsSubscription();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.foregroundUnsubscribe) {
      this.foregroundUnsubscribe();
      this.foregroundUnsubscribe = null;
    }
    this.clearSplashHideTimeout();
  }

  async logout() {
    this.errorMessage = null;
    this.teardownProfileSubscription();
    this.teardownUserPointsSubscription();
    try {
      await this.authService.logout();
      this.updateMenuAvailability(false);
    // resetOnboardingState removed
      this.cancelDailyCheckinRequest();
      await this.nativeBridge.removePreference('selectedWorkspace');
      this.username = 'Usuario';
      this.userAvatarKey = null;
      this.userAvatarPhoto = null;
      this.userWorkTypeLabel = null;
      this.userPoints = null;
      this.router.navigate(['/login']);
    } catch (error: any) {
      console.error('Logout failed:', error);
      this.errorMessage = error?.message || 'No pudimos cerrar la sesion. Intentalo nuevamente.';
    }
  }

  // Ensure resolveAvatarTheme always returns a valid AvatarTheme
  get avatarTheme(): AvatarTheme {
    return resolveAvatarTheme(this.userAvatarKey) ?? THEMES[0]; // Default to the first theme
  }
}
